import type { ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ROLE_LABELS, type Me } from '@wo/shared';
import { api } from '../lib/api';
import { useSettings } from '../lib/hooks';
import { DeviceRequestsBanner, useDeviceRequests } from './DeviceRequests';

const link = ({ isActive }: { isActive: boolean }) =>
  `rounded-md px-3 py-2 text-sm font-medium ${isActive ? 'bg-slate-900 text-white' : 'text-slate-200 hover:bg-slate-700'}`;

export function Layout({ me, children }: { me: Me; children: ReactNode }) {
  const settings = useSettings();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canApprove = me.role === 'ADMIN' || me.role === 'MANAGER';
  const waiting = useDeviceRequests(canApprove).data?.length ?? 0;

  const logout = async () => {
    await api.post('/auth/logout');
    qc.clear();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <nav className="bg-slate-800">
        <div className="mx-auto flex max-w-[1600px] flex-wrap items-center gap-1 px-4 py-2">
          <span className="mr-4 font-bold text-white">{settings.data?.appName ?? 'Work Orders'}</span>
          <NavLink to="/" end className={link}>Shop Board</NavLink>
          <NavLink to="/work-orders/new" className={link}>New Work Order</NavLink>
          <NavLink to="/work-orders" end className={link}>Work Orders</NavLink>
          <NavLink to="/customers" className={link}>Customers</NavLink>
          <NavLink to="/machines" className={link}>Machines</NavLink>
          <NavLink to="/reports" className={link}>Reports</NavLink>
          <NavLink to="/legacy" className={link}>Legacy</NavLink>
          {canApprove && <NavLink to="/admin/voids" className={link}>Void Approvals</NavLink>}
          {canApprove && <NavLink to="/admin/audit" className={link}>Audit Log</NavLink>}
          {canApprove && (
            <NavLink to="/admin/devices" className={link}>
              Devices
              {waiting > 0 && <span className="ml-1 rounded-full bg-amber-400 px-1.5 text-xs font-bold text-black">{waiting}</span>}
            </NavLink>
          )}
          {canApprove && <NavLink to="/admin/users" className={link}>Users</NavLink>}
          {canApprove && <NavLink to="/admin/email" className={link}>Email</NavLink>}
          <span className="ml-auto text-sm text-slate-300">
            {me.name} · {ROLE_LABELS[me.role]}
          </span>
          <button onClick={logout} className="ml-3 rounded-md px-3 py-2 text-sm text-slate-200 hover:bg-slate-700">
            Sign out
          </button>
        </div>
      </nav>
      {canApprove && <DeviceRequestsBanner />}
      <main className="mx-auto max-w-[1600px] p-4">{children}</main>
    </div>
  );
}
